import { useState } from 'react';
import { User, Lock, Moon, Sun, Save, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import authService from '../../services/authService';

function Section({ title, icon: Icon, children }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: 16,
      padding: '24px 28px',
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 16, height: 16, color: '#22c55e' }} />
        </div>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', disabled, rightEl }) {
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%', padding: '10px 14px', paddingRight: rightEl ? 42 : 14,
          borderRadius: 10, border: '1px solid var(--border)',
          background: 'var(--input-bg)', color: 'var(--text-primary)',
          fontSize: 14, outline: 'none', boxSizing: 'border-box',
          opacity: disabled ? 0.5 : 1,
        }}
      />
      {rightEl && (
        <button onClick={rightEl.onClick} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
          {rightEl.icon}
        </button>
      )}
    </div>
  );
}

function Toast({ type, msg }) {
  if (!msg) return null;
  const isErr = type === 'error';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: isErr ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border: `1px solid ${isErr ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, marginBottom: 16, fontSize: 13, color: isErr ? '#f87171' : '#4ade80' }}>
      {isErr ? <AlertCircle style={{ width: 15, height: 15, flexShrink: 0 }} /> : <CheckCircle style={{ width: 15, height: 15, flexShrink: 0 }} />}
      {msg}
    </div>
  );
}

export default function ProfilePage({ userData, darkMode, setDarkMode, onUserUpdate }) {
  // Profile fields
  const [firstName, setFirstName] = useState(userData?.firstName || '');
  const [lastName,  setLastName]  = useState(userData?.lastName  || '');
  const [phone,     setPhone]     = useState(userData?.phone     || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState({ type: '', text: '' });

  // Password fields
  const [currentPw,  setCurrentPw]  = useState('');
  const [newPw,      setNewPw]      = useState('');
  const [confirmPw,  setConfirmPw]  = useState('');
  const [showCurr,   setShowCurr]   = useState(false);
  const [showNew,    setShowNew]    = useState(false);
  const [pwSaving,   setPwSaving]   = useState(false);
  const [pwMsg,      setPwMsg]      = useState({ type: '', text: '' });

  const isDark = darkMode;

  const cssVars = {
    '--card-bg':      isDark ? '#0f1f30' : '#ffffff',
    '--border':       isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    '--input-bg':     isDark ? '#0a1628' : '#f9fafb',
    '--text-primary': isDark ? '#f1f5f9' : '#111827',
    '--text-muted':   isDark ? '#64748b' : '#6b7280',
    '--page-bg':      isDark ? '#0a1628' : '#f3f4f6',
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const result = await authService.updateProfile({ firstName, lastName, phone });
      onUserUpdate(result.user);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  const savePassword = async () => {
    setPwMsg({ type: '', text: '' });
    if (!currentPw || !newPw || !confirmPw) {
      setPwMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setPwSaving(true);
    try {
      await authService.changePassword(currentPw, newPw);
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div style={{ ...cssVars, minHeight: '100vh', background: 'var(--page-bg)', padding: '32px 24px', overflowY: 'auto' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>Account Settings</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {userData?.email}
            {userData?.accessTier && (
              <span style={{ marginLeft: 10, padding: '2px 8px', borderRadius: 12, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', color: '#22c55e', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {userData.accessTier}
              </span>
            )}
          </p>
        </div>

        {/* ── Appearance ── */}
        <Section title="Appearance" icon={isDark ? Moon : Sun}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Dark Mode</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Darker sidebar and backgrounds</p>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={{
                width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: darkMode ? '#22c55e' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: darkMode ? 25 : 3,
                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.25)', transition: 'left 0.2s',
              }} />
            </button>
          </div>
        </Section>

        {/* ── Profile Info ── */}
        <Section title="Profile Information" icon={User}>
          <Toast type={profileMsg.type} msg={profileMsg.text} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
            <Field label="First Name">
              <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
            </Field>
            <Field label="Last Name">
              <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
            </Field>
          </div>
          <Field label="Email">
            <Input value={userData?.email || ''} disabled placeholder="Email" />
          </Field>
          <Field label="Phone">
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+254712345678" />
          </Field>
          <button
            onClick={saveProfile}
            disabled={profileSaving}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 20px', borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: profileSaving ? 'not-allowed' : 'pointer', opacity: profileSaving ? 0.7 : 1 }}
          >
            <Save style={{ width: 14, height: 14 }} />
            {profileSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </Section>

        {/* ── Change Password ── */}
        <Section title="Change Password" icon={Lock}>
          <Toast type={pwMsg.type} msg={pwMsg.text} />
          <Field label="Current Password">
            <Input
              type={showCurr ? 'text' : 'password'}
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              placeholder="Your current password"
              rightEl={{ onClick: () => setShowCurr(v => !v), icon: showCurr ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} /> }}
            />
          </Field>
          <Field label="New Password">
            <Input
              type={showNew ? 'text' : 'password'}
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              placeholder="At least 8 characters"
              rightEl={{ onClick: () => setShowNew(v => !v), icon: showNew ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} /> }}
            />
          </Field>
          <Field label="Confirm New Password">
            <Input
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder="Repeat new password"
            />
          </Field>
          <button
            onClick={savePassword}
            disabled={pwSaving}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 20px', borderRadius: 10, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: pwSaving ? 'not-allowed' : 'pointer', opacity: pwSaving ? 0.7 : 1 }}
          >
            <Lock style={{ width: 14, height: 14 }} />
            {pwSaving ? 'Updating…' : 'Update Password'}
          </button>
        </Section>

      </div>
    </div>
  );
}
